import { useEffect, useRef } from 'react';

import { type AgreedLinePrice, type AgreedPriceRequestLine, resolveAgreedPrices } from '@api/vendors';

/**
 * What a line's price fields become once its agreed price is known. Shared by the purchase
 * order and production order forms so both say the same thing about the same price.
 */
export type AgreedPricePatch = {
    unitPrice: number | '';
    noAgreedPrice: boolean;
    /**
     * Shown under the price, and only when something needs acting on: the quantity is under
     * the vendor's minimum, or the price is agreed in another currency. A routine match says
     * nothing, so a normal line stays one row high.
     */
    priceNote?: string;
    /** The note is worth noticing: the quantity is under the vendor's minimum. */
    priceWarn: boolean;
    /** Which agreed line the figure is ("90-day credit · from 500"), for the price box's tooltip. */
    priceDetail?: string;
};

const formatQty = (n: number): string => n.toLocaleString('en-IN');

export const describeAgreedPrice = (resolved: AgreedLinePrice): AgreedPricePatch => {
    if (resolved.price == null) {
        if (resolved.otherCurrencies.length > 0) {
            return { unitPrice: '', noAgreedPrice: false, priceNote: `Agreed in ${resolved.otherCurrencies.join(' / ')}`, priceWarn: false, priceDetail: undefined };
        }
        return { unitPrice: '', noAgreedPrice: true, priceNote: undefined, priceWarn: false, priceDetail: undefined };
    }
    const parts = [
        resolved.termsName,
        resolved.minQty != null && resolved.minQty > 0 ? `from ${formatQty(resolved.minQty)}` : null,
    ].filter(Boolean);
    const priceDetail = parts.length > 0 ? parts.join(' · ') : undefined;
    if (resolved.belowMinimum && resolved.minimum != null) {
        return { unitPrice: resolved.price, noAgreedPrice: false, priceNote: `Below minimum ${formatQty(resolved.minimum)}`, priceWarn: true, priceDetail };
    }
    return { unitPrice: resolved.price, noAgreedPrice: false, priceNote: undefined, priceWarn: false, priceDetail };
};

/** A line the form may price: `key` is how the caller finds it again when the answer lands. */
export type AgreedPriceLine = AgreedPriceRequestLine & { key: string };

/**
 * Asks for the agreed price of every given line whenever the vendor, the currency, the payment
 * terms option or any line's item or quantity changes, and hands the answers back. The caller
 * passes only lines whose price the form filled in itself; a price somebody typed is theirs.
 *
 * Waits for typing to settle (a quantity of 1,000 is four keystrokes), and drops an answer
 * that a newer request has overtaken, so a slow reply cannot undo a later change.
 */
export const useAgreedLinePrices = (args: {
    enabled: boolean;
    vendorId: string | null;
    currency: string | null;
    termsOptionId: string | null;
    lines: AgreedPriceLine[];
    onResolved: (results: Array<{ key: string; quantity: number | undefined; patch: AgreedPricePatch }>) => void;
}): void => {
    const onResolved = useRef(args.onResolved);
    onResolved.current = args.onResolved;
    const latest = useRef(0);

    const signature = JSON.stringify([args.enabled, args.vendorId, args.currency, args.termsOptionId, args.lines]);

    useEffect(() => {
        if (!args.enabled || !args.vendorId || args.lines.length === 0) return;
        const request = ++latest.current;
        const lines = args.lines;
        const timer = setTimeout(() => {
            resolveAgreedPrices({
                vendorId: args.vendorId as string,
                ...(args.currency ? { currency: args.currency } : {}),
                ...(args.termsOptionId ? { termsOptionId: args.termsOptionId } : {}),
                lines: lines.map(({ itemType, itemId, quantity }) => ({ itemType, itemId, ...(quantity != null ? { quantity } : {}) })),
            })
                .then((results) => {
                    if (request !== latest.current || !Array.isArray(results)) return;
                    onResolved.current(
                        results.map((resolved, i) => ({ key: lines[i].key, quantity: lines[i].quantity, patch: describeAgreedPrice(resolved) })),
                    );
                })
                .catch(() => {
                    /* prices unavailable: the lines keep what they have and stay editable */
                });
        }, 300);
        return () => clearTimeout(timer);
        // The signature is the whole input; the individual fields are read through it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);
};
